import React from 'react';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';

// --- Import Pages/Components ---
import SignUpManagerPage from './pages/SignUpManagerPage';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import TaskDetailPage from './pages/TaskDetailPage';
import CreateTaskPage from './pages/CreateTaskPage';
import EmployeeListPage from './pages/EmployeeListPage';
import AddEmployeePage from './pages/AddEmployeePage';
import EditEmployeePage from './pages/EditEmployeePage';
import UserReportPage from './pages/UserReportPage';
import EditTaskPage from './pages/EditTaskPage';
import Layout from './components/Layout';
import ProtectedRoute from './components/ProtectedRoute';
import DefaultRedirector from './components/DefaultRedirector';
import ClientListPage from './pages/ClientListPage';
import AddClientPage from './pages/AddClientPage';
import EditClientPage from './pages/EditClientPage';
import CreateCampaignPage from './pages/CreateCampaignPage';
import CampaignListPage from './pages/CampaignListPage';
import CampaignPlanPage from './pages/CampaignPlanPage';
import EditCampaignPage from './pages/EditCampaignPage';
import NotificationsPage from './pages/NotificationsPage';
import ProfilePage from './pages/ProfilePage';
import CreateEventPage from './pages/CreateEventPage';
import ManageEventsPage from './pages/ManageEventsPage';
import EditEventPage from './pages/EditEventPage';
import ChatPage from './pages/ChatPage';
import ChatHistory from './pages/ChatHistory';
import TraineesPage from './pages/TraineesPage';
import TeamAccessPage from './pages/TeamAccessPage';
import TraineeTasksDashboardPage from './pages/TraineeTasksDashboardPage';
import EmployeeTasksPage from './pages/EmployeeTasksPage';
import ClientTasksPage from './pages/ClientTasksPage';
import TeamPerformancePage from './pages/TeamPerformancePage';
import TraineePerformanceReportPage from './pages/TraineePerformanceReportPage';
import DailyLogPage from './pages/DailyLogPage';
import ManagerDailyLogViewPage from './pages/ManagerDailyLogViewPage';
import StaffPerformanceReportPage from './pages/StaffPerformanceReportPage';
import CreateInvoicePage from './pages/CreateInvoicePage';
import InvoicesListPage from './pages/InvoicesListPage';
import InvoiceDetailPage from './pages/InvoiceDetailPage';
import EditInvoicePage from './pages/EditInvoicePage';
import CostSettingsPage from './pages/CostSettingsPage';
import ClientProfitabilityPage from './pages/ClientProfitabilityPage';
import WorkByPersonPage from './pages/WorkByPersonPage';
import LoginHistoryPage from './pages/LoginHistoryPage';
import LoginMapPage from './pages/LoginMapPage';
import AttendanceReportPage from './pages/AttendanceReportPage';
import WorkHoursManagementPage from './pages/WorkHoursManagementPage';
import CompanyResourcesPage from './pages/CompanyResourcesPage';
import ClientWeeklyWorkHubPage from './pages/ClientWeeklyWorkHubPage';
import PromotionCalculatorPage from './pages/PromotionCalculatorPage';
import Chat from './pages/Chat';

// Tools
import ToolsPage from "./pages/ToolsPage";
import QRManagerPage from "./pages/QRManagerPage";
import ImageToolsPage from "./pages/ImageToolsPage";

// Social Media Content Management
import SocialDashboardPage from "./pages/SocialDashboardPage";
import ClientSocialPagesPage from "./pages/ClientSocialPagesPage";
import ContentTasksPage from "./pages/ContentTasksPage";
import CreateContentTaskPage from "./pages/CreateContentTaskPage";
import ContentTaskDetailPage from "./pages/ContentTaskDetailPage";
import EditContentTaskPage from "./pages/EditContentTaskPage";
import ContentCalendarPage from "./pages/ContentCalendarPage";
import ContentApprovalsPage from "./pages/ContentApprovalsPage";
import SocialReportsPage from "./pages/SocialReportsPage";

// ✅ NEW: Fired account page
import AccountDisabledPage from "./pages/AccountDisabledPage";

// Public landing page
import LandingPage from "./pages/LandingPage";

// --- Define roles for specific sections ---
const clientManagementRoles = ['Manager', 'Sales Manager', 'Sales', 'Accounts Manager'];
const campaignCreationRoles = ['Manager', 'Sales Manager', 'Sales', 'Accounts Manager'];
const campaignManagementRoles = ['Manager', 'Sales Manager', 'Sales', 'Accounts Manager', 'Content Creator'];
const employeeManagementRoles = ['Manager', 'HR', 'Accounts Manager'];
const eventCreationRoles = ['Manager', 'Accounts Manager'];
const ClientWorkRoles = ['Manager', 'Accounts Manager', 'Sales Manager', 'Sales'];
const aiRoles = ['Manager', 'Sales Manager', 'Sales', 'Accounts Manager', 'Content Creator'];
const traineePageAccessAndManagementRoles = ['Manager', 'Accounts Manager'];
const traineeTasksDashboardAccessRoles = ['Manager', 'Accounts Manager', 'Trainee', 'Supervisor'];
const managerRoles = ['Manager'];
const managerAndAccountsAndSalesRoles = ['Manager', 'Accounts Manager', 'Sales Manager', 'Sales'];
const salesAccessRoles = ['Manager', 'Sales Manager', 'Sales'];
const invoiceEditRoles = ['Manager', 'Sales Manager', 'Sales', 'Accounts Manager'];

// Client profitability: Account Manager enters work; Manager + Sales Manager see financials.
const clientProfitabilityRoles = ['Manager', 'Sales Manager', 'Accounts Manager'];
// Financial/cost-bearing views (per-person cost breakdown) — Manager + Sales Manager only.
const moneyRoles = ['Manager', 'Sales Manager'];
// Cost & rate settings (salaries, freelancer rates) — Manager only.
const costSettingsRoles = ['Manager'];

// ✅ (Optional) Tools roles — غيرها حسب بدك
const toolsRoles = ['Manager', 'Accounts Manager', 'Sales Manager', 'Sales', 'HR'];

// Social Media Content Management roles
const socialManagementRoles = ['Manager', 'Accounts Manager', 'Sales Manager', 'Sales'];
const socialContentRoles = ['Manager', 'Accounts Manager', 'Sales Manager', 'Sales', 'Content Creator'];

function App() {
  return (
    <AuthProvider>
      <Router>
        <Routes>
          {/* Public Routes */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup-manager" element={<SignUpManagerPage />} />

          {/* ✅ NEW: Fired users landing page */}
          <Route path="/account-disabled" element={<AccountDisabledPage />} />

          {/* Public landing/marketing page */}
          <Route path="/about" element={<LandingPage />} />

          {/* Protected Routes inside Layout */}
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }
          >
            <Route index element={<DefaultRedirector />} />
            <Route path="dashboard" element={<DashboardPage />} />

            {/* ✅ Tools (FIXED: nested paths WITHOUT leading "/") */}
            <Route
              path="tools"
              element={
                <ProtectedRoute allowedRoles={toolsRoles}>
                  <ToolsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="tools/qr"
              element={
                <ProtectedRoute allowedRoles={toolsRoles}>
                  <QRManagerPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="tools/image-tools"
              element={
                <ProtectedRoute allowedRoles={toolsRoles}>
                  <ImageToolsPage />
                </ProtectedRoute>
              }
            />

            {/* Trainee Tasks Dashboard */}
            <Route
              path="trainee-tasks-dashboard"
              element={
                <ProtectedRoute allowedRoles={traineeTasksDashboardAccessRoles}>
                  <TraineeTasksDashboardPage />
                </ProtectedRoute>
              }
            />

            {/* Task Routes */}
            <Route path="task/new" element={<ProtectedRoute><CreateTaskPage /></ProtectedRoute>} />
            <Route path="task/:taskId" element={<ProtectedRoute><TaskDetailPage /></ProtectedRoute>} />
            <Route path="task/:taskId/edit" element={<ProtectedRoute><EditTaskPage /></ProtectedRoute>} />

            {/* Employee Routes */}
            <Route
              path="employees"
              element={
                <ProtectedRoute allowedRoles={employeeManagementRoles}>
                  <EmployeeListPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="employees/new"
              element={
                <ProtectedRoute allowedRoles={employeeManagementRoles}>
                  <AddEmployeePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="employees/:userId/edit"
              element={
                <ProtectedRoute allowedRoles={employeeManagementRoles}>
                  <EditEmployeePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="employees/:employeeId/tasks"
              element={
                <ProtectedRoute allowedRoles={['Manager', 'HR', 'Supervisor']}>
                  <EmployeeTasksPage />
                </ProtectedRoute>
              }
            />

            {/* Reports */}
            <Route
              path="reports/user-performance"
              element={
                <ProtectedRoute allowedRoles={['Manager']}>
                  <UserReportPage />
                </ProtectedRoute>
              }
            />

            {/* Client Routes */}
            <Route
              path="clients"
              element={
                <ProtectedRoute allowedRoles={clientManagementRoles}>
                  <ClientListPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="clients/new"
              element={
                <ProtectedRoute allowedRoles={clientManagementRoles}>
                  <AddClientPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="clients/:clientId/edit"
              element={
                <ProtectedRoute allowedRoles={clientManagementRoles}>
                  <EditClientPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="clients/:clientId/tasks"
              element={
                <ProtectedRoute allowedRoles={['Manager', 'Sales', 'Accounts Manager']}>
                  <ClientTasksPage />
                </ProtectedRoute>
              }
            />

            {/* Campaign Routes */}
            <Route
              path="campaigns"
              element={
                <ProtectedRoute allowedRoles={campaignManagementRoles}>
                  <CampaignListPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="campaigns/new"
              element={
                <ProtectedRoute allowedRoles={campaignCreationRoles}>
                  <CreateCampaignPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="campaigns/:campaignId/plan"
              element={
                <ProtectedRoute allowedRoles={campaignManagementRoles}>
                  <CampaignPlanPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="campaigns/:campaignId/edit"
              element={
                <ProtectedRoute allowedRoles={campaignManagementRoles}>
                  <EditCampaignPage />
                </ProtectedRoute>
              }
            />

            {/* Other Routes */}
            <Route path="notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />
            <Route path="profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
            <Route
              path="events"
              element={
                <ProtectedRoute allowedRoles={eventCreationRoles}>
                  <ManageEventsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="events/new"
              element={
                <ProtectedRoute allowedRoles={eventCreationRoles}>
                  <CreateEventPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="events/:eventId/edit"
              element={
                <ProtectedRoute allowedRoles={eventCreationRoles}>
                  <EditEventPage />
                </ProtectedRoute>
              }
            />

            <Route path="chat/:chatId" element={<ProtectedRoute allowedRoles={aiRoles}><ChatPage /></ProtectedRoute>} />
            <Route path="chat" element={<ProtectedRoute allowedRoles={aiRoles}><ChatPage /></ProtectedRoute>} />
            <Route path="chat/history" element={<ProtectedRoute allowedRoles={aiRoles}><ChatHistory /></ProtectedRoute>} />

            <Route path="trainees" element={<ProtectedRoute allowedRoles={traineePageAccessAndManagementRoles}><TraineesPage /></ProtectedRoute>} />
            <Route path="team-access" element={<ProtectedRoute allowedRoles={managerRoles}><TeamAccessPage /></ProtectedRoute>} />
            <Route path="team-performance" element={<ProtectedRoute allowedRoles={['Manager']}><TeamPerformancePage /></ProtectedRoute>} />
            <Route path="trainee-performance" element={<ProtectedRoute allowedRoles={['Manager']}><TraineePerformanceReportPage /></ProtectedRoute>} />

            <Route path="resources" element={<ProtectedRoute allowedRoles={eventCreationRoles}><CompanyResourcesPage /></ProtectedRoute>} />
            <Route path="client-weekly-work" element={<ProtectedRoute allowedRoles={ClientWorkRoles}><ClientWeeklyWorkHubPage /></ProtectedRoute>} />

            {/* Daily Log */}
            <Route path="daily-log" element={<ProtectedRoute><DailyLogPage /></ProtectedRoute>} />
            <Route path="growup-chat" element={<ProtectedRoute><Chat /></ProtectedRoute>} />
            <Route path="manager/daily-logs" element={<ProtectedRoute allowedRoles={managerRoles}><ManagerDailyLogViewPage /></ProtectedRoute>} />

            <Route path="staff-performance" element={<ProtectedRoute allowedRoles={managerRoles}><StaffPerformanceReportPage /></ProtectedRoute>} />

            {/* Invoices */}
            <Route path="invoices" element={<ProtectedRoute allowedRoles={managerAndAccountsAndSalesRoles}><InvoicesListPage /></ProtectedRoute>} />
            <Route path="invoices/new" element={<ProtectedRoute allowedRoles={managerAndAccountsAndSalesRoles}><CreateInvoicePage /></ProtectedRoute>} />
            <Route path="invoices/:invoiceId" element={<ProtectedRoute allowedRoles={salesAccessRoles}><InvoiceDetailPage /></ProtectedRoute>} />
            <Route path="invoices/edit/:invoiceId" element={<ProtectedRoute allowedRoles={invoiceEditRoles}><EditInvoicePage /></ProtectedRoute>} />

            {/* Client profitability + cost settings */}
            <Route path="cost-settings" element={<ProtectedRoute allowedRoles={costSettingsRoles}><CostSettingsPage /></ProtectedRoute>} />
            <Route path="client-profitability" element={<ProtectedRoute allowedRoles={clientProfitabilityRoles}><ClientProfitabilityPage /></ProtectedRoute>} />
            <Route path="client-profitability/:clientId" element={<ProtectedRoute allowedRoles={clientProfitabilityRoles}><ClientProfitabilityPage /></ProtectedRoute>} />
            <Route path="work-by-person" element={<ProtectedRoute allowedRoles={moneyRoles}><WorkByPersonPage /></ProtectedRoute>} />

            {/* Login history/map */}
            <Route path="login-history" element={<ProtectedRoute allowedRoles={managerRoles}><LoginHistoryPage /></ProtectedRoute>} />
            <Route path="login-map" element={<ProtectedRoute allowedRoles={managerRoles}><LoginMapPage /></ProtectedRoute>} />

            {/* Attendance */}
            <Route path="attendance-report" element={<ProtectedRoute allowedRoles={managerRoles}><AttendanceReportPage /></ProtectedRoute>} />

            {/* Promotions */}
            <Route path="promotions" element={<ProtectedRoute allowedRoles={managerRoles}><PromotionCalculatorPage /></ProtectedRoute>} />

            {/* Work hours */}
            <Route path="work-hours" element={<ProtectedRoute allowedRoles={['Manager']}><WorkHoursManagementPage /></ProtectedRoute>} />

            {/* Social Media Content Management */}
            <Route
              path="social/dashboard"
              element={
                <ProtectedRoute allowedRoles={socialManagementRoles}>
                  <SocialDashboardPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="social/pages"
              element={
                <ProtectedRoute allowedRoles={socialManagementRoles}>
                  <ClientSocialPagesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="social/tasks"
              element={
                <ProtectedRoute allowedRoles={socialContentRoles}>
                  <ContentTasksPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="social/tasks/new"
              element={
                <ProtectedRoute allowedRoles={socialContentRoles}>
                  <CreateContentTaskPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="social/tasks/:taskId"
              element={
                <ProtectedRoute allowedRoles={socialContentRoles}>
                  <ContentTaskDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="social/tasks/:taskId/edit"
              element={
                <ProtectedRoute allowedRoles={socialContentRoles}>
                  <EditContentTaskPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="social/calendar"
              element={
                <ProtectedRoute allowedRoles={socialManagementRoles}>
                  <ContentCalendarPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="social/approvals"
              element={
                <ProtectedRoute allowedRoles={socialManagementRoles}>
                  <ContentApprovalsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="social/reports"
              element={
                <ProtectedRoute allowedRoles={socialManagementRoles}>
                  <SocialReportsPage />
                </ProtectedRoute>
              }
            />

            {/* 404 */}
            <Route
              path="*"
              element={
                <div style={{ textAlign: 'center', padding: '50px' }}>
                  <h2>404 - Page Not Found</h2>
                  <p>Sorry, the page you are looking for does not exist.</p>
                  <Link to="/">Go to Home</Link>
                </div>
              }
            />
          </Route>
        </Routes>
      </Router>
    </AuthProvider>
  );
}

export default App;
